<script setup lang="ts">
import { useRouter } from 'vue-router'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconChevronRight, IconLogout, IconScroll, IconUsers } from '../components/Icons'
import { Button, EmptyState, ErrorState, Panel, StatCard } from '../components/ui'
import { actionName, relativeTime, shortID } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'
import { useSession } from '../lib/session'

const router = useRouter()
const { logout } = useSession()
useTitle('概览')

const { data, error, initial, reload } = useAsync(async () => {
  const [users, audit] = await Promise.all([api.listUsers('', 1, 0), api.listAudit(8, 0)])
  return {
    userTotal: users.total,
    recent: audit.entries,
    auditTotal: audit.total,
  }
})
</script>

<template>
  <PageHeader title="概览" subtitle="账号权益与操作动态一览">
    <template #actions>
      <Button size="md" variant="primary" @click="router.push('/users')">管理账号</Button>
      <Button size="md" @click="logout">
        <IconLogout :size="15" />
        退出登录
      </Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel v-if="error">
      <ErrorState :message="error.message" @retry="reload" />
    </Panel>
    <template v-else-if="initial">
      <div class="stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div class="panel h-[96px] animate-pulse bg-raised/60" />
        <div class="panel h-[96px] animate-pulse bg-raised/60" />
        <div class="panel h-[96px] animate-pulse bg-raised/60" />
      </div>
    </template>
    <template v-else-if="data">
      <div class="stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label="账号"
          :value="data.userTotal"
          tone="accent"
          hint="已注册用户总数"
          :icon="IconUsers"
        />
        <StatCard
          label="审计记录"
          :value="data.auditTotal"
          tone="default"
          hint="后台操作留痕"
          :icon="IconScroll"
        />
        <StatCard
          label="最近动态"
          :value="data.recent.length"
          unit="条"
          tone="ok"
          hint="本页展示的最近操作"
        />
      </div>

      <div class="mt-5 grid gap-4 xl:grid-cols-[1.4fr_0.8fr]">
        <Panel title="最近操作">
          <template #actions>
            <Button size="sm" variant="ghost" @click="router.push('/audit')">全部日志</Button>
          </template>
          <EmptyState v-if="data.recent.length === 0" title="暂无记录" />
          <ul v-else class="divide-y divide-line">
            <li
              v-for="e in data.recent"
              :key="e.id"
              class="flex items-start justify-between gap-3 px-4 py-3 text-[13.5px] transition-colors hover:bg-hover/70"
            >
              <span class="min-w-0">
                <span class="font-medium text-text">{{ actionName(e.action) }}</span>
                <span class="mt-0.5 block truncate text-[12.5px] text-dim">
                  {{ e.actor_email || shortID(e.actor_id ?? '') }} · {{ e.target_type }}
                  {{ e.target_id }}
                </span>
              </span>
              <span class="shrink-0 text-[12px] text-dim">{{ relativeTime(e.created_at) }}</span>
            </li>
          </ul>
        </Panel>

        <Panel title="快捷入口">
          <div class="flex flex-col gap-2 p-3">
            <button
              type="button"
              class="panel-lift flex items-center gap-3 rounded-lg border border-line bg-raised/40 px-3.5 py-3 text-left"
              @click="router.push('/users')"
            >
              <span class="flex size-9 items-center justify-center rounded-lg bg-accent/12 text-accent-dim">
                <IconUsers :size="16" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[14px] font-semibold">账号</span>
                <span class="block text-[12px] text-dim">搜索、查看权益与会话</span>
              </span>
              <IconChevronRight :size="16" class="text-dim" />
            </button>
            <button
              type="button"
              class="panel-lift flex items-center gap-3 rounded-lg border border-line bg-raised/40 px-3.5 py-3 text-left"
              @click="router.push('/audit')"
            >
              <span class="flex size-9 items-center justify-center rounded-lg bg-link/10 text-link">
                <IconScroll :size="16" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-[14px] font-semibold">审计日志</span>
                <span class="block text-[12px] text-dim">追溯管理员操作</span>
              </span>
              <IconChevronRight :size="16" class="text-dim" />
            </button>
          </div>
        </Panel>
      </div>
    </template>
  </PageBody>
</template>
